/**
 * AddSessionLogModal — admin-only picker for authoring a session log on a
 * tutor's behalf.
 *
 * Session logs are stored 1:1 against a scheduled session (document id = the
 * `scheduled_sessions` id), so an admin cannot invent a free-standing log. This
 * modal walks the admin through picking the tutor + date window, then lists that
 * tutor's scheduled sessions with their log status so a missing log can be
 * filled in (or an existing one corrected).
 *
 * Selecting a session hands off to the regular session-log form opened with
 * `?adminEntry=1`, which keeps the tutor's ownership while recording the admin
 * as the actual author.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiAlertTriangle, FiEdit3, FiExternalLink, FiLoader, FiPlus, FiX } from "react-icons/fi";

import {
  fetchActiveTutors,
  fetchAllStudents,
  fetchScheduledSessions,
} from "../admin/scheduling/schedulingApi";
import { fetchSessionLogsBySessionIds, toDateMaybe } from "../tutor/session_logs/sessionLogApi";

import "./AddSessionLogModal.css";

const DEFAULT_RANGE_DAYS = 30;

function toDateKey(value) {
  const d = toDateMaybe(value);
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function defaultRange() {
  const end = new Date();
  const start = new Date(end.getTime() - DEFAULT_RANGE_DAYS * 24 * 60 * 60 * 1000);
  return { start: toDateKey(start), end: toDateKey(end) };
}

function formatDate(value) {
  const d = toDateMaybe(value);
  if (!d) return "—";
  return d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric", year: "numeric" });
}

function formatTime(value) {
  const d = toDateMaybe(value);
  if (!d) return "—";
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function statusClass(status) {
  const s = String(status || "").toLowerCase();
  if (s === "present") return "asl-status--present";
  if (s === "confirmed") return "asl-status--confirmed";
  if (s === "cancelled" || s === "canceled") return "asl-status--cancelled";
  if (s === "no show" || s === "no-show") return "asl-status--noshow";
  return "asl-status--pending";
}

/** Open the tutor session-log form in admin-entry mode. */
export function openAdminSessionLogForm(session) {
  if (!session?.id) return;
  try {
    localStorage.setItem(`tutorSession_${session.id}`, JSON.stringify(session));
    localStorage.setItem(`sessionLog_${session.id}`, JSON.stringify(session));
  } catch (err) {
    console.warn("Could not stage session for the log form.", err);
  }
  window.open(`/tutor-session-form?id=${session.id}&adminEntry=1`, "_blank");
}

export default function AddSessionLogModal({ open, onClose }) {
  const defaults = useMemo(defaultRange, []);

  const [tutors, setTutors] = useState([]);
  const [tutorsLoading, setTutorsLoading] = useState(true);
  const [tutorUid, setTutorUid] = useState("");
  const [startDate, setStartDate] = useState(defaults.start);
  const [endDate, setEndDate] = useState(defaults.end);
  const [studentQuery, setStudentQuery] = useState("");

  const [sessions, setSessions] = useState([]);
  const [logBySessionId, setLogBySessionId] = useState({});
  const [studentNamesById, setStudentNamesById] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // ── Load tutors + students once, when the modal is first opened ──────────────
  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    setTutorsLoading(true);

    Promise.all([fetchActiveTutors(), fetchAllStudents()])
      .then(([tutorRows, studentRows]) => {
        if (!alive) return;
        const list = (tutorRows || [])
          .map((t) => ({ uid: t.id, name: String(t.name || t.email || "").trim() }))
          .filter((t) => t.name)
          .sort((a, b) => a.name.localeCompare(b.name));
        setTutors(list);
        setTutorUid((prev) => prev || list[0]?.uid || "");

        const map = {};
        (studentRows || []).forEach((s) => {
          if (s?.id) map[s.id] = String(s.name || "").trim();
        });
        setStudentNamesById(map);
      })
      .catch((err) => {
        console.error("Failed to load tutors for the session log picker", err);
        if (alive) setError("Could not load the tutor list right now.");
      })
      .finally(() => { if (alive) setTutorsLoading(false); });

    return () => { alive = false; };
  }, [open]);

  // ── Load the selected tutor's sessions (and their log status) ────────────────
  useEffect(() => {
    if (!open || !tutorUid || !startDate || !endDate) {
      setSessions([]);
      setLogBySessionId({});
      return undefined;
    }

    const rangeStart = new Date(`${startDate}T00:00:00`);
    const rangeEnd = new Date(`${endDate}T23:59:59.999`);
    if (Number.isNaN(rangeStart.getTime()) || Number.isNaN(rangeEnd.getTime())) {
      setSessions([]);
      setLogBySessionId({});
      return undefined;
    }

    let alive = true;
    setLoading(true);
    setError("");

    (async () => {
      try {
        const rows = await fetchScheduledSessions({ rangeStart, rangeEnd, tutorUid });
        if (!alive) return;

        const ordered = [...(rows || [])].sort(
          (a, b) => (toDateMaybe(b?.start)?.getTime() || 0) - (toDateMaybe(a?.start)?.getTime() || 0)
        );
        const ids = ordered.map((s) => s.id).filter(Boolean);
        const logs = ids.length ? await fetchSessionLogsBySessionIds(ids) : {};
        if (!alive) return;

        setSessions(ordered);
        setLogBySessionId(logs || {});
      } catch (err) {
        if (!alive) return;
        console.error("Failed to load sessions for the session log picker", err);
        setSessions([]);
        setLogBySessionId({});
        setError("Could not load this tutor's sessions right now.");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => { alive = false; };
  }, [open, tutorUid, startDate, endDate]);

  // ── Derived list ─────────────────────────────────────────────────────────────
  const studentNameOf = useCallback(
    (session) =>
      String(session?.studentName || studentNamesById[session?.studentId] || "").trim() || "Unknown student",
    [studentNamesById]
  );

  const visibleSessions = useMemo(() => {
    const needle = studentQuery.trim().toLowerCase();
    if (!needle) return sessions;
    return sessions.filter((s) => studentNameOf(s).toLowerCase().includes(needle));
  }, [sessions, studentQuery, studentNameOf]);

  // Only sessions that have already ended can be "missing" a log — a session
  // scheduled for later today or next week hasn't happened yet.
  const missingCount = useMemo(() => {
    const now = Date.now();
    return visibleSessions.filter((s) => {
      const ended = toDateMaybe(s?.end)?.getTime();
      if (!ended || ended >= now) return false;
      return !logBySessionId[s.id];
    }).length;
  }, [visibleSessions, logBySessionId]);

  if (!open) return null;

  const selectedTutor = tutors.find((t) => t.uid === tutorUid) || null;

  return (
    <div className="asl-overlay" role="dialog" aria-modal="true" aria-label="Add session log">
      <div className="asl-panel">
        <div className="asl-header">
          <div>
            <h2>Add Session Log</h2>
            <p>Pick a tutor and a session, then fill in the log on their behalf.</p>
          </div>
          <button type="button" className="asl-close" onClick={onClose} aria-label="Close">
            <FiX size={16} />
          </button>
        </div>

        <div className="asl-filters">
          <div className="asl-field">
            <label htmlFor="asl-tutor">Tutor</label>
            <select
              id="asl-tutor"
              value={tutorUid}
              onChange={(e) => setTutorUid(e.target.value)}
              disabled={tutorsLoading}
            >
              {tutorsLoading && <option value="">Loading tutors…</option>}
              {!tutorsLoading && !tutors.length && <option value="">No tutors available</option>}
              {tutors.map((t) => (
                <option key={t.uid} value={t.uid}>{t.name}</option>
              ))}
            </select>
          </div>

          <div className="asl-field">
            <label htmlFor="asl-start">From</label>
            <input
              id="asl-start"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div className="asl-field">
            <label htmlFor="asl-end">To</label>
            <input
              id="asl-end"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>

          <div className="asl-field">
            <label htmlFor="asl-student">Student</label>
            <input
              id="asl-student"
              type="search"
              placeholder="Filter by student"
              value={studentQuery}
              onChange={(e) => setStudentQuery(e.target.value)}
            />
          </div>
        </div>

        {error && (
          <div className="asl-error">
            <FiAlertTriangle size={14} /> {error}
          </div>
        )}

        <div className="asl-meta">
          {loading
            ? "Loading sessions…"
            : !tutorUid
              ? "Select a tutor to see their sessions."
              : `${visibleSessions.length} session${visibleSessions.length === 1 ? "" : "s"} · ${missingCount} missing log${
                  missingCount === 1 ? "" : "s"
                }`}
        </div>

        <div className="asl-listWrap">
          {loading && (
            <div className="asl-empty">
              <FiLoader className="asl-spin" size={16} /> Loading sessions…
            </div>
          )}

          {!loading && !visibleSessions.length && (
            <div className="asl-empty">
              No scheduled sessions found for {selectedTutor?.name || "this tutor"} in the selected date range.
            </div>
          )}

          {!loading && visibleSessions.map((session) => {
            const log = logBySessionId[session.id];
            const hasEnded = (toDateMaybe(session?.end)?.getTime() || 0) < Date.now();
            const logState = log ? "done" : hasEnded ? "missing" : "upcoming";
            return (
              <div key={session.id} className={`asl-row ${log ? "asl-row--hasLog" : ""}`}>
                <div className="asl-rowMain">
                  <div className="asl-rowDate">{formatDate(session.start)}</div>
                  <div className="asl-rowTime">
                    {formatTime(session.start)} – {formatTime(session.end)}
                  </div>
                  <div className="asl-rowStudent">{studentNameOf(session)}</div>
                  <div className="asl-rowSubject">{String(session.subject || "No subject")}</div>
                </div>

                <div className="asl-rowSide">
                  <span className={`asl-status ${statusClass(session.status)}`}>
                    {String(session.status || "Pending")}
                  </span>
                  <span className={`asl-logState asl-logState--${logState}`}>
                    {logState === "done" ? "Log submitted" : logState === "missing" ? "Log missing" : "Upcoming"}
                  </span>
                  <button
                    type="button"
                    className={`asl-action ${log ? "asl-action--ghost" : ""}`}
                    onClick={() => openAdminSessionLogForm(session)}
                    title={log ? "Open and edit this session log" : "Add a session log for this session"}
                  >
                    {log ? <FiExternalLink size={12} /> : <FiPlus size={12} />}
                    {log ? "Open Log" : "Add Log"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="asl-footer">
          <span className="asl-hint">
            <FiEdit3 size={12} /> Logs open in a new tab and are credited to the tutor.
          </span>
          <button type="button" className="asl-btn" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}
